import React from "react";
import { Box } from "@mui/material";

/**
 * Swipeable card pager (mobile).
 *
 * Chunks `items` into pages of `pageSize`, stacks each page vertically, and
 * pages horizontally on swipe. Paging uses native CSS scroll-snap rather than a
 * gesture library, so momentum/rubber-banding feel native on iOS and Android.
 * The dots below track the active page and can also be tapped to jump.
 *
 * Two layout rules keep the pager tight:
 *
 *  - Each page is a grid of equal rows (`gridAutoRows: 1fr`), so every card on a
 *    page is as tall as the tallest one instead of hugging its own content.
 *  - The scroller is pinned to the ACTIVE page's height. A flex/grid row is
 *    otherwise as tall as its tallest child, so a final short page (2 of 5
 *    cards) left the dots stranded below a block of white space.
 *
 * @param {Array}    items
 * @param {Function} renderItem  (item, index) => node
 * @param {number}  [pageSize]   cards per page
 * @param {number}  [gap]        theme spacing between cards in a page
 */
export default function CardSlider({
  items = [],
  renderItem = () => null,
  pageSize = 3,
  gap = 3,
}) {
  const scrollerRef = React.useRef(null);
  const pageRefs = React.useRef([]);
  const [page, setPage] = React.useState(0);
  const [activeHeight, setActiveHeight] = React.useState();

  const pages = React.useMemo(() => {
    const chunks = [];
    for (let i = 0; i < items.length; i += pageSize) {
      chunks.push(items.slice(i, i + pageSize));
    }
    return chunks;
  }, [items, pageSize]);

  // Searching/filtering rebuilds the list — jump back to the first page so the
  // user isn't left on a page that no longer exists.
  React.useEffect(() => {
    setPage(0);
    if (scrollerRef.current) scrollerRef.current.scrollLeft = 0;
  }, [items.length, pageSize]);

  const measureActivePage = React.useCallback(() => {
    const el = pageRefs.current[page];
    if (el) setActiveHeight(el.offsetHeight);
  }, [page]);

  // Height follows the page the user is on — measured rather than computed, so
  // it stays correct however the cards reflow.
  React.useLayoutEffect(() => {
    measureActivePage();
  }, [measureActivePage, pages]);

  // Rotation, font swaps and late-arriving content all change a page's height.
  React.useEffect(() => {
    if (typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(measureActivePage);
    pageRefs.current.forEach((el) => el && observer.observe(el));

    return () => observer.disconnect();
  }, [measureActivePage, pages.length]);

  const handleScroll = () => {
    const el = scrollerRef.current;
    if (!el || !el.clientWidth) return;
    const next = Math.round(el.scrollLeft / el.clientWidth);
    setPage((current) => (current === next ? current : next));
  };

  const goToPage = (index) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: index * el.clientWidth, behavior: "smooth" });
  };

  if (!pages.length) return null;

  return (
    <Box>
      <Box
        ref={scrollerRef}
        onScroll={handleScroll}
        sx={{
          display: "flex",
          alignItems: "flex-start",
          overflowX: "auto",
          // Taller neighbouring pages must not bleed past the active page.
          overflowY: "hidden",
          height: activeHeight,
          transition: "height 0.25s ease",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
          // Hide the scrollbar — the dots are the affordance here.
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {pages.map((pageItems, pageIndex) => (
          <Box
            key={pageIndex}
            ref={(el) => {
              pageRefs.current[pageIndex] = el;
            }}
            sx={{
              flex: "0 0 100%",
              minWidth: 0,
              scrollSnapAlign: "start",
              // Equal rows: every card on the page matches the tallest one. In a
              // content-height grid, `1fr` rows all resolve to the largest row.
              display: "grid",
              gridAutoRows: "1fr",
              gap,
            }}
          >
            {pageItems.map((item, itemIndex) =>
              renderItem(item, pageIndex * pageSize + itemIndex)
            )}
          </Box>
        ))}
      </Box>

      {pages.length > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            mt: 3,
          }}
        >
          {pages.map((_, index) => {
            const active = index === page;
            return (
              <Box
                key={index}
                component="button"
                type="button"
                aria-label={`Go to page ${index + 1}`}
                aria-current={active}
                onClick={() => goToPage(index)}
                sx={{
                  p: 0,
                  width: 9,
                  height: 9,
                  borderRadius: "50%",
                  cursor: "pointer",
                  border: "1px solid",
                  borderColor: active ? "primary.clr-300" : "neutral.clr-300",
                  bgcolor: active ? "primary.clr-300" : "transparent",
                  transition: "background-color 0.2s, border-color 0.2s",
                }}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
}
